import { query } from "gql-query-builder";
import type { IActiveProductDetails, IImagesList, ITotalImagesCount } from "../constants/apiconstants-pyxis";
import type { IPaginationRequest } from "../constants/pagination";
import Env from "../utils/env";
import { postUri } from "../utils/fetch";

export function getProductNameByOrgIdQuery(
	orgID: string,
	pagination: IPaginationRequest = { page: 0, page_size: 3 },
	abortSignal?: AbortSignal,
): Promise<IActiveProductDetails> {
	const publishedQuery = {
		operation: { name: "find_vendor_product_listings_by_org_id", alias: "publishedProducts" },
		variables: {
			orgId: { name: "org_id", value: Number(orgID), required: true, type: "Int" },
			sortBy: {
				name: "sort_by",
				value: { field: "last_update_date", order: "DESC" },
				required: false,
				type: "[SortBy]",
			},
			page: { value: pagination.page },
			page_size: { value: pagination.page_size },
			publishedFilter: {
				name: "filter",
				value: { and: [{ published: { eq: true } }, { deleted: { ne: true } }] },
				required: true,
				type: "ProductListingFilter",
			},
		},
		fields: [
			{ error: ["status", "detail"] },
			{
				data: [
					"_id",
					"category",
					"cert_projects",
					"org_id",
					{ published_history: [{ history: ["date"] }] },
					{ contacts: ["type", "email_address"] },
					{ descriptions: ["long", "short"] },
					{ faqs: ["answer", "question"] },
					{ features: ["description", "title"] },
					"functional_categories",
					{ legal: ["description", "license_agreement_url", "privacy_policy_url"] },
					{ linked_resources: ["description", "title", "type", "url"] },
					{ logo: ["url"] },
					"name",
					"published",
					"deleted",
					{ quick_start_configuration: ["instructions", "default_deploy_instructions"] },
					"repositories",
					{ search_aliases: ["key", "value"] },
					{ support: ["description", "email_address", "phone_number", "url"] },
					{ product_validations_data: [{ validations: ["_id"] }] },
					"type",
					"certification_badges_all",
					"certification_badges",
					"certification_level",
					"deployed_as_all",
					"deployed_as",
					"creation_date",
					"last_update_date",
					"target_platforms",
				],
			},
		],
	};

	const unpublishedQuery = {
		operation: { name: "find_vendor_product_listings_by_org_id", alias: "unfinishedProducts" },
		variables: {
			orgId: { name: "org_id", value: Number(orgID), required: true, type: "Int" },
			sortBy: {
				name: "sort_by",
				value: { field: "last_update_date", order: "DESC" },
				required: false,
				type: "[SortBy]",
			},
			page: { value: pagination.page },
			page_size: { value: pagination.page_size },
			unPublishedFilter: {
				name: "filter",
				value: { and: [{ published: { ne: true } }, { deleted: { ne: true } }] },
				required: true,
				type: "ProductListingFilter",
			},
		},
		fields: [
			{ error: ["status", "detail"] },
			{
				data: [
					"_id",
					"category",
					"cert_projects",
					"org_id",
					{ contacts: ["type", "email_address"] },
					{ descriptions: ["long", "short"] },
					{ faqs: ["answer", "question"] },
					{ features: ["description", "title"] },
					"functional_categories",
					{ legal: ["description", "license_agreement_url", "privacy_policy_url"] },
					{ linked_resources: ["description", "title", "type", "url"] },
					{ logo: ["url"] },
					"name",
					"published",
					"deleted",
					{ quick_start_configuration: ["instructions", "default_deploy_instructions"] },
					"repositories",
					{ search_aliases: ["key", "value"] },
					{ support: ["description", "email_address", "phone_number", "url"] },
					"type",
					"certification_badges_all",
					"certification_badges",
					"certification_level",
					"deployed_as_all",
					"deployed_as",
					"creation_date",
					"last_update_date",
					"target_platforms",
					{ product_validations_data: [{ validations: ["_id"] }] },
				],
			},
		],
	};

	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	const productListQuery = query([unpublishedQuery, publishedQuery]);
	return postUri(Env.pyxisGraphqlAPIURL!, productListQuery, additionalParams);
}

export function findLowGradeImagesByOrgId(
	paginationObj: IPaginationRequest = { page: 1, page_size: 10 },
	filerObject: any,
	sortObject: any,
	abortSignal?: AbortSignal,
): Promise<IImagesList> {
	const lowGradeImagesListQuery = query({
		operation: { name: "find_images", alias: "lowGradeImagesList" },
		variables: {
			sort_by: { name: "sort_by", value: sortObject, required: true, type: "[SortBy]" },
			filter: { name: "filter", value: filerObject, required: true, type: "ContainerImageFilter" },
			pageNumber: { name: "page", value: paginationObj.page - 1, required: true, type: "Int" },
			pageSize: { name: "page_size", value: paginationObj.page_size, required: true, type: "Int" },
		},
		fields: [
			{ error: ["status", "detail"] },
			{
				data: [
					"_id",
					"deleted",
					"certified",
					{ freshness_grades: ["grade", "start_date", "end_date", "creation_date"] },
					{
						edges: [
							{
								cert_project: [
									{
										data: [
											"name",
											"_id",
											"project_status",
											{ edges: [{ product_listings: [{ data: ["name", "_id"] }] }] },
										],
									},
								],
							},
						],
					},
				],
			},
			"total",
		],
	});
	const additionalParams: RequestInit = {};
	if (abortSignal) {
		Object.assign(additionalParams, { signal: abortSignal });
	}
	return postUri(Env.pyxisGraphqlAPIURL!, lowGradeImagesListQuery, additionalParams);
}

export function findImagesCountByOrgId(
	paginationObj: IPaginationRequest = { page: 1, page_size: 1 },
	filerObject: any,
): Promise<ITotalImagesCount> {
	const ImagesCount = query({
		operation: { name: "find_images", alias: "totalImagesCountByOrgId" },
		variables: {
			filter: { name: "filter", value: filerObject, required: true, type: "ContainerImageFilter" },
			pageNumber: { name: "page", value: paginationObj.page - 1, required: true, type: "Int" },
			pageSize: { name: "page_size", value: paginationObj.page_size, required: true, type: "Int" },
		},
		fields: [{ error: ["status", "detail"] }, { data: ["_id"] }, "total"],
	});
	return postUri(Env.pyxisGraphqlAPIURL!, ImagesCount);
}
